import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, BookOpen, Heart, GraduationCap, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { institutionInfo, departments, programmes, highlights, newsEvents, images, applicationFormUrl, partners, prospectusUrl } from '../data/mock';

const getDeptById = (deptId) => {
  for (let i = 0; i < departments.length; i++) {
    if (departments[i].id === deptId) return departments[i];
  }
  return null;
};

const Home = () => {
  const featuredProgrammes = programmes.slice(0, 6);
  const highlightIcons = [Award, BookOpen, Users, Heart];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.hero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#142942]/70 via-[#142942]/50 to-[#142942]/80" />
        </div>
        
        <div className="container-custom relative z-10 pt-48 pb-20">
          <div className="max-w-3xl">
            <Badge className="bg-[#d4a855] text-[#142942] mb-6 text-sm font-medium px-4 py-1">
              NACTE Accredited Institution
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight animate-fade-in-up">
              Welcome to BIT
            </h1>
            <p className="text-[#d4a855] text-xl md:text-2xl font-medium mb-2 animate-fade-in-up stagger-1">
              Baobab Institute of Tanzania
            </p>
            <p className="text-white/90 text-lg md:text-xl mb-8 leading-relaxed animate-fade-in-up stagger-2">
              A beacon of educational excellence nurturing empowered individuals capable of transforming challenges into opportunities—with a particular focus on <span className="text-[#d4a855] font-semibold">securing the digital future</span>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all">
                  Apply Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/programmes">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] text-lg px-8 py-6 rounded-full">
                  Explore Programmes
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#d4a855] rounded-full" />
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, index) => {
              const Icon = highlightIcons[index];
              return (
                <Card key={index} className="bg-white border-none shadow-lg card-hover group">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#2563eb] transition-colors">
                      <Icon className="w-8 h-8 text-[#d4a855]" />
                    </div>
                    <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mb-1">{item.title}</h3>
                    <p className="text-[#d4a855] text-sm font-medium mb-2">{item.subtitle}</p>
                    <p className="text-[#475569] text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Departments Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Our Departments
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Explore our four specialized departments offering comprehensive programmes to shape your future career.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept) => (
              <Link key={dept.id} to={`/departments/${dept.id}`}>
                <Card className="h-full overflow-hidden card-hover group cursor-pointer border-none shadow-lg">
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={dept.image}
                      alt={dept.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f] to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="font-display font-semibold text-white text-lg leading-tight">
                        {dept.shortName}
                      </h3>
                    </div>
                  </div>
                  <CardContent className="py-4">
                    <p className="text-[#475569] text-sm line-clamp-2 mb-3">
                      {dept.description}
                    </p>
                    <div className="flex gap-2">
                      {dept.intakes.map((intake) => (
                        <Badge key={intake} variant="outline" className="text-xs border-[#2563eb] text-[#2563eb]">
                          {intake} Intake
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/departments">
              <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                View All Departments
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Intakes Banner */}
      <section className="bg-[#1e3a5f] py-12">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Admission Intakes
              </h2>
              <p className="text-white/80">
                Two main intakes per year for most programmes
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center min-w-[180px]">
                <Calendar className="w-10 h-10 text-[#d4a855] mx-auto mb-3" />
                <h3 className="text-white font-semibold text-xl mb-1">March Intake</h3>
                <p className="text-white/70 text-sm">Business, Hotel & Tourism, Science & Technology</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center min-w-[180px]">
                <Calendar className="w-10 h-10 text-[#d4a855] mx-auto mb-3" />
                <h3 className="text-white font-semibold text-xl mb-1">September Intake</h3>
                <p className="text-white/70 text-sm">All Departments including Health & Allied Sciences</p>
              </div>
            </div>
          </div>
          <p className="text-center text-[#d4a855] text-sm mt-6">
            <strong>Note:</strong> Health & Allied Sciences programmes are available only in the September intake.
          </p>
        </div>
      </section>

      {/* Featured Programmes */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Featured Programmes
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Explore our industry-aligned programmes designed to prepare you for a successful career.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProgrammes.map((programme) => {
              const dept = getDeptById(programme.departmentId);
              return (
                <Link key={programme.id} to={`/programmes/${programme.id}`}>
                  <Card className="h-full card-hover cursor-pointer border-none shadow-md hover:shadow-xl">
                    <CardHeader className="pb-2">
                      <Badge className="w-fit bg-[#1e3a5f] text-white text-xs mb-2">
                        {dept?.shortName}
                      </Badge>
                      <CardTitle className="text-[#1e3a5f] font-display text-lg leading-tight">
                        {programme.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-[#475569] text-sm line-clamp-2 mb-4">
                        {programme.overview}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline" className="text-xs">
                          NTA Level {programme.ntaLevels.join('-')}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {programme.duration}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        {programme.intakes.map((intake) => (
                          <span key={intake} className="text-xs text-[#2563eb] font-medium">
                            {intake} Intake
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/programmes">
              <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
                View All Programmes
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* News & Events */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              News & Events
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Stay updated with the latest happenings at BIT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {newsEvents.slice(0, 3).map((item) => {
              const getEventLink = (id) => {
                if (id === 'bit-fashion-show-and-modeling-event') return '/news/bit-fashion-show-and-modeling-event';
                if (id === 'bit-football-match-campus-sports-event') return '/news/bit-football-match-campus-sports-event';
                return '/news';
              };
              const eventLink = getEventLink(item.id);
              return (
                <Card key={item.id} className="overflow-hidden card-hover border-none shadow-md">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#1e3a5f]/20" />
                    {item.category && (
                      <Badge className="absolute top-3 left-3 bg-[#d4a855] text-[#142942]">
                        {item.category}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="pt-4">
                    <span className="text-[#d4a855] text-sm font-medium">{item.date}</span>
                    <h3 className="font-display font-semibold text-[#1e3a5f] mt-2 mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-[#475569] text-sm line-clamp-2 mb-4">
                      {item.excerpt}
                    </p>
                    <Link to={eventLink} className="text-[#2563eb] font-medium text-sm flex items-center group">
                      Read More
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/news">
              <Button variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
                View All News
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-12 bg-[#f8fafc]">
        <div className="container-custom">
          <p className="text-center text-[#475569] text-sm mb-8">Our Partners & Stakeholders</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {partners.map((partner, index) => (
              <a 
                key={index} 
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#94a3b8] font-semibold text-base md:text-lg hover:text-[#1e3a5f] transition-colors"
              >
                {partner.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Join thousands of students building their future at Baobab Institute of Tanzania.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold px-8">
                Apply Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href={prospectusUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8">
                Download Prospectus
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
